import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  ArrowLeft,
  Trophy,
  ChatCircleText,
  CheckCircle,
  Users,
  CircleNotch,
  ShieldWarning,
  SmileySad,
  ArrowsClockwise,
  Phone,
  PhoneCall,
} from "@phosphor-icons/react";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "7 days" },
  { key: "month", label: "30 days" },
  { key: "all", label: "All-time" },
];

function initials(name) {
  const src = (name || "?").trim();
  return src.split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

function medal(rank) {
  if (rank === 1) return { label: "🥇", ring: "ring-[#FBBF24]" };
  if (rank === 2) return { label: "🥈", ring: "ring-[#9CA3AF]" };
  if (rank === 3) return { label: "🥉", ring: "ring-[#D97706]" };
  return { label: `#${rank}`, ring: "ring-[var(--border)]" };
}

function StatCard({ icon: Icon, label, value, tint, testId }) {
  return (
    <div
      className="rounded-lg border border-[var(--border)] p-4 bg-[var(--card)] flex items-center gap-3"
      data-testid={testId}
    >
      <div
        className="h-10 w-10 rounded-lg grid place-items-center shrink-0"
        style={{ background: `${tint}1a`, color: tint }}
      >
        <Icon size={20} weight="duotone" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-[var(--muted)] font-semibold">
          {label}
        </div>
        <div className="text-2xl font-semibold heading leading-tight">{value}</div>
      </div>
    </div>
  );
}

export default function Reports() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = (p = period) => {
    setLoading(true);
    setErr(null);
    api
      .get(`/reports/agents/extended?period=${p}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setErr(e?.response?.data?.detail || e.message || "Load failed");
        setData(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const agents = data?.agents || [];
  const totals = data?.totals || {
    messages_sent: 0,
    tickets_closed: 0,
    active_agents: 0,
    complaints_resolved: 0,
    dissatisfied_count: 0,
  };
  const maxMsgs = useMemo(
    () => Math.max(1, ...agents.map((a) => a.messages_sent || 0)),
    [agents],
  );

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--fg)]" data-testid="reports-page">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-[var(--border)] bg-[var(--card)]/90 backdrop-blur supports-[backdrop-filter]:bg-[var(--card)]/70">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            data-testid="reports-back-btn"
            aria-label="Back to Inbox"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <Trophy size={22} weight="fill" className="text-[#FBBF24] shrink-0" />
            <div className="min-w-0">
              <div className="text-base sm:text-lg font-semibold heading leading-tight truncate">
                Agent Reports
              </div>
              <div className="text-[11px] text-[var(--muted)] leading-tight">
                Replies, ticket closures, complaints resolved & customer dissatisfaction.
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(period)}
              disabled={loading}
              data-testid="reports-refresh-btn"
              className="gap-1.5 h-9"
            >
              {loading ? (
                <CircleNotch size={14} className="animate-spin" />
              ) : (
                <ArrowsClockwise size={14} />
              )}
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Period tabs */}
        <div
          className="flex items-center gap-1 text-xs flex-wrap"
          data-testid="reports-period-tabs"
        >
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3.5 py-2 rounded-md transition-colors ${
                period === p.key
                  ? "bg-[var(--fg)] text-[var(--bg)] font-medium"
                  : "hover:bg-[var(--card)] text-[var(--muted)] border border-[var(--border)]"
              }`}
              data-testid={`reports-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          <StatCard
            icon={ChatCircleText}
            label="Messages"
            value={totals.messages_sent}
            tint="#60A5FA"
            testId="reports-stat-messages"
          />
          <StatCard
            icon={CheckCircle}
            label="Tickets closed"
            value={totals.tickets_closed}
            tint="#10B981"
            testId="reports-stat-tickets"
          />
          <StatCard
            icon={ShieldWarning}
            label="Complaints resolved"
            value={totals.complaints_resolved}
            tint="#F59E0B"
            testId="reports-stat-complaints"
          />
          <StatCard
            icon={SmileySad}
            label="Dissatisfied"
            value={totals.dissatisfied_count}
            tint="#EF4444"
            testId="reports-stat-dissatisfied"
          />
          <StatCard
            icon={Phone}
            label="Calls dialed"
            value={totals.calls_dialed || 0}
            tint="#8B5CF6"
            testId="reports-stat-calls-dialed"
          />
          <StatCard
            icon={PhoneCall}
            label="Calls connected"
            value={totals.calls_connected || 0}
            tint="#34D399"
            testId="reports-stat-calls-connected"
          />
          <StatCard
            icon={Users}
            label="Active agents"
            value={totals.active_agents}
            tint="#A78BFA"
            testId="reports-stat-active"
          />
        </div>

        {/* Leaderboard table */}
        <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between">
            <div>
              <div className="font-semibold heading text-sm">Leaderboard</div>
              <div className="text-[11px] text-[var(--muted)]">
                Ranked by (closed + resolved − dissatisfied), tie-break by messages sent.
              </div>
            </div>
            <div className="text-[11px] text-[var(--muted)]">
              {agents.length} agent{agents.length === 1 ? "" : "s"}
            </div>
          </div>

          {loading ? (
            <div className="py-12 flex items-center justify-center text-[var(--muted)] text-sm gap-2">
              <CircleNotch size={18} className="animate-spin" /> Loading…
            </div>
          ) : err ? (
            <div className="py-12 text-center text-sm text-[#EF4444]">{err}</div>
          ) : agents.length === 0 ? (
            <div className="py-12 text-center text-sm text-[var(--muted)]">
              No data for this period.
            </div>
          ) : (
            <div className="divide-y divide-[var(--border)]" data-testid="reports-agents-list">
              {agents.map((a) => {
                const m = medal(a.rank);
                const barWidth = `${Math.round(((a.messages_sent || 0) / maxMsgs) * 100)}%`;
                return (
                  <div
                    key={a.agent_id}
                    className="px-4 py-3.5 flex items-center gap-3 hover:bg-[var(--bg)] transition-colors"
                    data-testid={`reports-agent-row-${a.agent_id}`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ring-2 ${m.ring} bg-[var(--bg)] shrink-0`}
                    >
                      <span className="text-base">{m.label}</span>
                    </div>
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarFallback className="bg-[var(--fg)] text-[var(--bg)] text-xs">
                        {initials(a.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-sm truncate">{a.full_name}</div>
                        {a.role && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg)] text-[var(--muted)] uppercase border border-[var(--border)]">
                            {a.role}
                          </span>
                        )}
                      </div>
                      {a.email && (
                        <div className="text-[11px] text-[var(--muted)] truncate">{a.email}</div>
                      )}
                      <div className="mt-1.5 h-1.5 bg-[var(--bg)] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#60A5FA] rounded-full transition-all"
                          style={{ width: barWidth }}
                        />
                      </div>
                    </div>
                    <div className="hidden md:grid grid-cols-6 gap-3 text-xs shrink-0 pl-2">
                      <div className="text-center min-w-[60px]">
                        <div className="font-semibold text-sm heading leading-tight">
                          {a.messages_sent}
                        </div>
                        <div className="text-[10px] text-[var(--muted)]">replies</div>
                      </div>
                      <div className="text-center min-w-[60px]">
                        <div className="font-semibold text-sm heading leading-tight text-[#10B981]">
                          {a.tickets_closed}
                        </div>
                        <div className="text-[10px] text-[var(--muted)]">closed</div>
                      </div>
                      <div className="text-center min-w-[60px]">
                        <div className="font-semibold text-sm heading leading-tight text-[#F59E0B]">
                          {a.complaints_resolved}
                        </div>
                        <div className="text-[10px] text-[var(--muted)]">resolved</div>
                      </div>
                      <div className="text-center min-w-[70px]">
                        <div
                          className={`font-semibold text-sm heading leading-tight ${
                            a.dissatisfied_count > 0 ? "text-[#EF4444]" : ""
                          }`}
                        >
                          {a.dissatisfied_count}
                        </div>
                        <div className="text-[10px] text-[var(--muted)]">dissatisfied</div>
                      </div>
                      <div className="text-center min-w-[60px]">
                        <div className="font-semibold text-sm heading leading-tight text-[#8B5CF6]">
                          {a.calls_dialed || 0}
                        </div>
                        <div className="text-[10px] text-[var(--muted)]">calls</div>
                      </div>
                      <div className="text-center min-w-[80px]">
                        <div className="font-semibold text-sm heading leading-tight text-[#34D399]">
                          {a.calls_connected || 0}
                          {a.calls_dialed > 0 && (
                            <span className="text-[10px] text-[var(--muted)] font-normal ml-1">
                              ({Math.round(((a.calls_connected || 0) / a.calls_dialed) * 100)}%)
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-[var(--muted)]">connected</div>
                      </div>
                    </div>
                    {/* Mobile: stacked stats */}
                    <div className="md:hidden flex flex-col items-end gap-0.5 text-[10px] shrink-0">
                      <div>
                        <span className="font-semibold text-sm">{a.messages_sent}</span>{" "}
                        <span className="text-[var(--muted)]">replies</span>
                      </div>
                      <div>
                        <span className="font-semibold text-[#10B981]">{a.tickets_closed}</span>{" "}
                        closed ·{" "}
                        <span className="font-semibold text-[#F59E0B]">{a.complaints_resolved}</span>{" "}
                        resolved
                      </div>
                      <div>
                        <span className="font-semibold text-[#8B5CF6]">{a.calls_dialed || 0}</span>{" "}
                        calls ·{" "}
                        <span className="font-semibold text-[#34D399]">
                          {a.calls_connected || 0}
                        </span>{" "}
                        connected
                      </div>
                      <div className={a.dissatisfied_count > 0 ? "text-[#EF4444]" : ""}>
                        <span className="font-semibold">{a.dissatisfied_count}</span>{" "}
                        dissatisfied
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4 text-[12px] text-[var(--muted)] space-y-1.5">
          <div className="flex items-start gap-2">
            <ChatCircleText size={14} className="mt-0.5 text-[#60A5FA] shrink-0" />
            <div>
              <span className="font-medium text-[var(--fg)]">replies</span> — outgoing messages
              sent by this agent (bot/system excluded).
            </div>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle size={14} className="mt-0.5 text-[#10B981] shrink-0" />
            <div>
              <span className="font-medium text-[var(--fg)]">closed</span> — tickets the agent
              marked as resolved.
            </div>
          </div>
          <div className="flex items-start gap-2">
            <ShieldWarning size={14} className="mt-0.5 text-[#F59E0B] shrink-0" />
            <div>
              <span className="font-medium text-[var(--fg)]">resolved</span> — customer
              complaints the agent marked resolved (manual or via close).
            </div>
          </div>
          <div className="flex items-start gap-2">
            <SmileySad size={14} className="mt-0.5 text-[#EF4444] shrink-0" />
            <div>
              <span className="font-medium text-[var(--fg)]">dissatisfied</span> — times a
              customer tapped <em>"I am not satisfied"</em> in the close-feedback bot for a
              chat this agent closed. Lower is better.
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Phone size={14} className="mt-0.5 text-[#8B5CF6] shrink-0" />
            <div>
              <span className="font-medium text-[var(--fg)]">calls</span> — Tata click-to-call
              attempts the agent successfully dialed in this period.
            </div>
          </div>
          <div className="flex items-start gap-2">
            <PhoneCall size={14} className="mt-0.5 text-[#34D399] shrink-0" />
            <div>
              <span className="font-medium text-[var(--fg)]">connected</span> — calls where the
              customer actually answered + a conversation took place (Tata hangup webhook
              reported <code className="font-mono text-[10px]">duration_seconds &gt; 0</code>).
              Percentage shown is connect-rate (connected ÷ dialed).
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
